import React, { useEffect, useState } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import ButtonGeneral from '../general/buttons/buttonGeneral';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid, esES, GridRowHeightParams } from '@mui/x-data-grid';
import { useAuthContext } from '../../../contexts/authContext';

type Props = {
    title?: string;
    titleButtonAdd?: string;
    startIconButtonAdd?: JSX.Element | string;
    onClickAdd?: any;
    onClickBack?: () => void | null;
    columns?: any;
    rows?: any;
    rowCount?: number;
    loading?: boolean;
    checkboxSelection?: boolean | false;
    onSetPage?: any;
    disableRow?: boolean | false;
    disableAdd?: boolean | false;
    disabledAdd?: boolean | false;
    disableBack?: boolean | false;
    enabledSearch?: boolean | false;
    searchField?: JSX.Element | string;
    disableDataGrid?: false | boolean;
    titleButtonGeneral?: string;
    startIconButtonGeneral?: JSX.Element | string;
    onClickButtonGeneral?: any;
    loadingButtonGeneral?: boolean | false;
    idButtonGeneral?: string;
    enabledButtonGeneral?: boolean | false;
    enabledHelpButton?: boolean | false;
    titleHelpButton?: string;
    startIconHelpButton?: JSX.Element | string;
    onClickHelpButton?: any;
    idHelpButton?: string;
    idButtonAdd?: string;
    dataGridContainerId?: string;
    enabledSecondButtonGeneral?: boolean | false;
    titleSecondButtonGeneral?: string;
    startIconSecondButtonGeneral?: JSX.Element | string;
    onClickSecondButtonGeneral?: any;
    enabledButtonSecondary?: boolean | false;
    titleButtonSecondary?: string;
    startIconButtonSecondary?: JSX.Element | string;
    onClickButtonSecondary?: any;
    loadingButtonSecondary?: boolean | false;
    idButtonSecondary?: string;
    sxButtonSecondary?: any;
    sxProps?: boolean | false;
    message?: JSX.Element | string;
    isRowSelectable?: any;
    onRowSelection?: (values: string[]) => void;
    rowSelection?: any;
    filterAvanced?: false | boolean;
    onFilterAvanced?: any;
    showList?: boolean;
    getRowHeight?: GridRowHeightParams | 'auto';
};

const ListView = ({
    title,
    titleButtonAdd,
    startIconButtonAdd,
    onClickAdd,
    onClickBack,
    columns,
    rows,
    rowCount,
    checkboxSelection,
    disableRow,
    disableAdd,
    disabledAdd,
    disableBack,
    disableDataGrid,
    onSetPage,
    loading,
    enabledSearch,
    searchField,
    titleButtonGeneral,
    startIconButtonGeneral,
    onClickButtonGeneral,
    loadingButtonGeneral,
    idButtonGeneral,
    enabledButtonGeneral,
    enabledHelpButton,
    titleHelpButton,
    startIconHelpButton,
    onClickHelpButton,
    idHelpButton,
    idButtonAdd,
    dataGridContainerId,
    enabledSecondButtonGeneral,
    titleSecondButtonGeneral,
    startIconSecondButtonGeneral,
    onClickSecondButtonGeneral,
    enabledButtonSecondary,
    titleButtonSecondary,
    startIconButtonSecondary,
    onClickButtonSecondary,
    loadingButtonSecondary,
    idButtonSecondary,
    sxButtonSecondary,
    message,
    sxProps,
    isRowSelectable,
    onRowSelection,
    rowSelection,
    filterAvanced,
    onFilterAvanced,
    showList = true,
    getRowHeight,
}: Props) => {
    const { modeTheme } = useAuthContext();

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 20,
        page: 0,
    });
    const [rowsIds, setRowsIds] = useState<string[]>([]);

    useEffect(() => {
        if (!disableDataGrid && onSetPage) {
            onSetPage(paginationModel);
        }
        if (checkboxSelection) {
            if (rowsIds.length === 0 && rowSelection.length > 0) {
                setRowsIds(rowSelection);
            }
            onRowSelection!(rowsIds);
        }
    }, [
        disableDataGrid,
        onSetPage,
        paginationModel,
        rowSelection,
        checkboxSelection,
        rowsIds,
        rows,
    ]);

    const validateFilter = (newModel: any) => {
        if (newModel) {
            const data = newModel.items[0] || [];
            if (data?.value) {
                onFilterAvanced(data);
            } else {
                onFilterAvanced(null);
            }
        }
    };

    return (
        <>
            {title && (
                <>
                    <Typography
                        color="primary"
                        component="h3"
                        sx={{
                            flexGrow: 1,
                            fontSize: { lg: 24, md: 22, xs: 20 },
                            color:
                                modeTheme === 'darkMode' ? '#000' : undefined,
                        }}
                    >
                        {title}
                    </Typography>
                    <hr color="#9F9E9EFF" />
                </>
            )}
            <Grid container sx={{ minWidth: 300, mt: 2 }}>
                {enabledSearch && (
                    <Grid item xs={12} sm={6.5} md={6.5} lg={6.5}>
                        {searchField}
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    sm={enabledSearch ? 5.5 : 12}
                    md={enabledSearch ? 5.5 : 12}
                    lg={enabledSearch ? 5.5 : 12}
                    sx={{
                        mt: {
                            xs: enabledSearch ? 2 : 0,
                            sm: 0,
                        },
                        textAlign: 'right',
                    }}
                >
                    {enabledButtonSecondary === true && (
                        <ButtonGeneral
                            id={idButtonSecondary}
                            onClick={onClickButtonSecondary}
                            startIcon={startIconButtonSecondary || ''}
                            title={titleButtonSecondary || ''}
                            loading={loadingButtonSecondary}
                            sx={
                                sxButtonSecondary || {
                                    mr: {
                                        xs: 1,
                                        sm: 1,
                                    },
                                    mt: { xs: 1, sm: 1 },
                                    backgroundColor: 'error.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'error.dark',
                                    },
                                }
                            }
                        />
                    )}
                    {enabledSecondButtonGeneral === true && (
                        <ButtonGeneral
                            onClick={onClickSecondButtonGeneral}
                            startIcon={startIconSecondButtonGeneral || ''}
                            title={titleSecondButtonGeneral || ''}
                            sx={{
                                mr: {
                                    xs: enabledSecondButtonGeneral ? 1 : 0,
                                    sm: 1,
                                },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                        />
                    )}
                    {enabledButtonGeneral === true && (
                        <ButtonGeneral
                            id={idButtonGeneral}
                            onClick={onClickButtonGeneral}
                            startIcon={startIconButtonGeneral || ''}
                            title={titleButtonGeneral || ''}
                            loading={loadingButtonGeneral}
                            sx={{
                                mr: {
                                    xs: enabledSecondButtonGeneral ? 0 : 1,
                                    sm: 1,
                                },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                        />
                    )}
                    {enabledHelpButton === true && (
                        <ButtonGeneral
                            id={idHelpButton}
                            onClick={onClickHelpButton}
                            startIcon={startIconHelpButton || ''}
                            title={titleHelpButton || ''}
                            sx={{
                                mr: { xs: 1, sm: 1 },
                                mt: { xs: 1, sm: 1 },
                                backgroundColor: 'transparent',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                boxShadow: 'none',
                                color: 'primary.main',
                                '& .MuiTypography-root': {
                                    color: 'primary.main',
                                },
                                '& .MuiButton-startIcon .MuiSvgIcon-root': {
                                    color: 'primary.main',
                                },
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        />
                    )}
                    {disableAdd !== true && (
                        <ButtonGeneral
                            id={idButtonAdd}
                            onClick={onClickAdd}
                            startIcon={startIconButtonAdd || ''}
                            title={titleButtonAdd || ''}
                            sx={{
                                mr: { xs: 1, sm: 1 },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                            disabled={disabledAdd}
                        />
                    )}
                    {disableBack !== true && (
                        <ButtonGeneral
                            onClick={onClickBack}
                            startIcon={<ArrowBackIcon color="secondary" />}
                            title="Volver"
                            sx={{
                                mr: { xs: 0, sm: 1 },
                                mt: { xs: enabledButtonGeneral && 1, sm: 1 },
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            {message && message}
            {!disableDataGrid && (
                <Box
                    id={dataGridContainerId}
                    mt={2.5}
                    sx={{ minWidth: 300 }}
                    display={showList ? 'block' : 'none'}
                >
                    <DataGrid
                        localeText={
                            esES.components.MuiDataGrid.defaultProps.localeText
                        }
                        rows={rows || []}
                        columns={columns}
                        getRowHeight={() => getRowHeight && 'auto'}
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        paginationMode="server"
                        loading={loading}
                        pageSizeOptions={[20, 50, 100]}
                        onFilterModelChange={(newModel) =>
                            filterAvanced && validateFilter(newModel)
                        }
                        onPaginationModelChange={setPaginationModel}
                        checkboxSelection={checkboxSelection}
                        disableRowSelectionOnClick={disableRow}
                        isRowSelectable={isRowSelectable}
                        rowSelectionModel={checkboxSelection ? rowsIds : []}
                        onRowSelectionModelChange={(rows: any) => {
                            setRowsIds(rows);
                        }}
                        sx={
                            sxProps
                                ? {}
                                : {
                                    '.MuiTablePagination-displayedRows': {
                                        marginTop: '1rem',
                                    },
                                    '.MuiTablePagination-selectLabel': {
                                        marginTop: '1rem',
                                    },
                                    '.MuiDataGrid-columnHeaders': {
                                        color: 'secondary.main',
                                        backgroundColor: 'primary.main',
                                    },
                                    '.MuiDataGrid-sortIcon': {
                                        color: 'secondary.main',
                                    },
                                    '.MuiSvgIcon-fontSizeSmall': {
                                        color: 'secondary.main',
                                    },
                                    '.MuiDataGrid-iconSeparator': {
                                        color: 'secondary.main',
                                    },
                                    '.MuiDataGrid-footerContainer': {
                                        display: !onSetPage ? 'none' : 'flex',
                                    },
                                }
                        }
                    />
                </Box>
            )}
        </>
    );
};

export default ListView;
